import { useState, useEffect } from 'react';
import {
  useAffiliateDashboard,
  useAffiliates,
  useUpdateAffiliateStatus,
  usePayouts,
  useProcessPayout,
  useCreateAffiliate,
  useAdminUsers,
  useAdminTeams,
} from '@/hooks/useAdmin';
import {
  AdminAffiliateFilters,
  AdminAffiliateListItem,
  AdminPayoutFilters,
  AdminPayoutListItem,
} from '@/types/admin';
import { Combobox } from '@/components/ui/Combobox';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ActionButton } from '@/components/admin/ActionButton';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';
import { ConfirmDialog, PromptDialog, Card, Badge, EmptyState, Pagination, Input, Modal } from '@/components/ui';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { Select } from '@/components/ui/Select';
import { formatApiError } from '@/services/api';

/**
 * Admin Affiliates Page
 * Manage affiliates and process payouts
 */

type TabType = 'affiliates' | 'payouts';

const AFFILIATE_STATUSES = ['active', 'suspended', 'inactive'] as const;
const PAYOUT_STATUSES = ['requested', 'approved', 'paid', 'rejected'] as const;

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-orange-100 text-orange-700',
  inactive: 'bg-gray-100 text-gray-700',
  requested: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  paid: 'bg-primary-50 text-primary-800',
  rejected: 'bg-red-100 text-red-700',
};

export const AdminAffiliates = () => {
  const { formatDateTime, formatDateOnly } = useTimezone();
  const [activeTab, setActiveTab] = useState<TabType>('affiliates');
  const [showCreateAffiliateModal, setShowCreateAffiliateModal] = useState(false);

  // Affiliate filters
  const [affiliateFilters, setAffiliateFilters] = useState<AdminAffiliateFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const debouncedAffiliateSearch = useDebounce(affiliateSearch, 300);
  useEffect(() => {
    setAffiliateFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedAffiliateSearch]);

  // Payout filters
  const [payoutFilters, setPayoutFilters] = useState<AdminPayoutFilters>({
    page: 1,
    limit: 20,
    sortBy: 'requestedAt',
    sortOrder: 'desc',
  });

  // Dialog states
  const [statusChangeDialog, setStatusChangeDialog] = useState<{
    open: boolean;
    affiliate: AdminAffiliateListItem | null;
    newStatus: string | null;
  }>({ open: false, affiliate: null, newStatus: null });

  const [payoutActionDialog, setPayoutActionDialog] = useState<{
    open: boolean;
    payout: AdminPayoutListItem | null;
    action: 'approve' | 'reject' | 'pay' | null;
  }>({ open: false, payout: null, action: null });

  const [showPayoutNotesDialog, setShowPayoutNotesDialog] = useState(false);

  // Queries
  const { data: dashboardData, isLoading: dashboardLoading } = useAffiliateDashboard();
  const {
    data: affiliatesData,
    isLoading: affiliatesLoading,
    error: affiliatesError,
  } = useAffiliates({ ...affiliateFilters, search: debouncedAffiliateSearch || undefined });
  const {
    data: payoutsData,
    isLoading: payoutsLoading,
    error: payoutsError,
  } = usePayouts(payoutFilters);

  // Mutations
  const updateStatus = useUpdateAffiliateStatus();
  const processPayout = useProcessPayout();

  // Handlers
  const handleAffiliateSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleStatusChange = async (
    affiliate: AdminAffiliateListItem,
    newStatus: (typeof AFFILIATE_STATUSES)[number]
  ) => {
    if (newStatus === affiliate.status) return;

    // Open dialog to get reason
    setStatusChangeDialog({
      open: true,
      affiliate,
      newStatus,
    });
  };

  const handleStatusChangeSubmit = async (reason: string) => {
    if (!statusChangeDialog.affiliate || !statusChangeDialog.newStatus) return;

    try {
      await updateStatus.mutateAsync({
        id: statusChangeDialog.affiliate.id,
        data: {
          status: statusChangeDialog.newStatus as (typeof AFFILIATE_STATUSES)[number],
          reason: reason || undefined,
        },
      });
      toast.success(`Status updated to ${statusChangeDialog.newStatus}`);
      setStatusChangeDialog({ open: false, affiliate: null, newStatus: null });
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleProcessPayout = async (
    payout: AdminPayoutListItem,
    action: 'approve' | 'reject' | 'pay'
  ) => {
    // Open confirmation dialog
    setPayoutActionDialog({
      open: true,
      payout,
      action,
    });
  };

  const handlePayoutConfirm = () => {
    const { action } = payoutActionDialog;

    // If approve, process immediately (no notes needed)
    if (action === 'approve') {
      handlePayoutActionSubmit('');
      return;
    }

    // For reject/pay, show notes dialog
    setShowPayoutNotesDialog(true);
  };

  const handlePayoutActionSubmit = async (adminNotes: string) => {
    if (!payoutActionDialog.payout || !payoutActionDialog.action) return;

    const { payout, action } = payoutActionDialog;

    // Validate rejection reason
    if (action === 'reject' && !adminNotes.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await processPayout.mutateAsync({
        id: payout.id,
        data: { action, adminNotes: adminNotes || undefined },
      });
      toast.success(
        action === 'approve'
          ? 'Payout approved'
          : action === 'reject'
          ? 'Payout rejected'
          : 'Payout marked as paid'
      );
      setPayoutActionDialog({ open: false, payout: null, action: null });
      setShowPayoutNotesDialog(false);
    } catch (err) {
      toast.error('Failed to process payout');
    }
  };

  const handleAffiliatePageChange = (newPage: number) => {
    setAffiliateFilters({ ...affiliateFilters, page: newPage });
  };

  const handlePayoutPageChange = (newPage: number) => {
    setPayoutFilters({ ...payoutFilters, page: newPage });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Affiliates"
        subtitle="Manage affiliates and process payouts"
        action={
          <Button onClick={() => setShowCreateAffiliateModal(true)}>
            <i className={`hm hm-${ICONS.PLUS} mr-2`} />
            Create Affiliate
          </Button>
        }
      />

      {/* Stats Cards */}
      {dashboardLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg border border-gray-200">
              <LoadingSpinner />
            </div>
          ))}
        </div>
      ) : dashboardData ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatCard
            label="Total Affiliates"
            value={dashboardData.totalAffiliates}
            color="text-primary-700"
          />
          <StatCard
            label="Active Affiliates"
            value={dashboardData.activeAffiliates}
            color="text-green-600"
          />
          <StatCard
            label="Total Referrals"
            value={dashboardData.totalReferrals}
            color="text-secondary-500"
          />
          <StatCard
            label="Total Commissions"
            value={`$${(dashboardData.totalCommissions ?? 0).toFixed(2)}`}
            color="text-primary-800"
          />
          <StatCard
            label="Pending Payouts"
            value={`$${(dashboardData.pendingPayouts ?? 0).toFixed(2)}`}
            color="text-orange-600"
          />
        </div>
      ) : null}

      {/* Tabs - pill style */}
      <div className="bg-gray-100/80 backdrop-blur-sm p-1.5 rounded-2xl flex gap-1">
        {[
          { id: 'affiliates' as const, label: 'Affiliates', icon: ICONS.USERS },
          { id: 'payouts' as const, label: 'Payouts', icon: ICONS.CREDIT_CARD },
        ].map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'group flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl font-medium text-sm transition-all duration-200',
                isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon
                name={tab.icon}
                size="sm"
                color={isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}
                ariaHidden
              />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <Card padding="none">

        {activeTab === 'affiliates' ? (
          <AffiliatesTab
            filters={affiliateFilters}
            setFilters={setAffiliateFilters}
            search={affiliateSearch}
            setSearch={setAffiliateSearch}
            data={affiliatesData}
            isLoading={affiliatesLoading}
            error={affiliatesError}
            onSearch={handleAffiliateSearch}
            onStatusChange={handleStatusChange}
            onPageChange={handleAffiliatePageChange}
            formatDateTime={formatDateTime}
            formatDateOnly={formatDateOnly}
            updateStatusPending={updateStatus.isPending}
          />
        ) : (
          <PayoutsTab
            filters={payoutFilters}
            setFilters={setPayoutFilters}
            data={payoutsData}
            isLoading={payoutsLoading}
            error={payoutsError}
            onProcessPayout={handleProcessPayout}
            onPageChange={handlePayoutPageChange}
            formatDateTime={formatDateTime}
            formatDateOnly={formatDateOnly}
            processPayoutPending={processPayout.isPending}
          />
        )}
      </Card>

      {/* Status Change Dialog */}
      {statusChangeDialog.affiliate && statusChangeDialog.newStatus && (
        <PromptDialog
          open={statusChangeDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setStatusChangeDialog({ open: false, affiliate: null, newStatus: null });
            }
          }}
          onConfirm={handleStatusChangeSubmit}
          title={`Change Status to ${statusChangeDialog.newStatus.toUpperCase()}`}
          description={`Why are you changing the status to ${statusChangeDialog.newStatus}?`}
          placeholder="Enter reason..."
          confirmText="Update Status"
          required={false}
          inputType="textarea"
        />
      )}

      {/* Payout Action Dialogs */}
      {payoutActionDialog.payout && payoutActionDialog.action && (
        <>
          {/* Step 1: Confirm action */}
          {!showPayoutNotesDialog && (
            <ConfirmDialog
              open={payoutActionDialog.open}
              onOpenChange={(open) => {
                if (!open) {
                  setPayoutActionDialog({ open: false, payout: null, action: null });
                }
              }}
              onConfirm={handlePayoutConfirm}
              title={
                payoutActionDialog.action === 'approve'
                  ? 'Approve Payout'
                  : payoutActionDialog.action === 'reject'
                  ? 'Reject Payout'
                  : 'Mark as Paid'
              }
              description={
                payoutActionDialog.action === 'approve'
                  ? `Approve payout of $${(payoutActionDialog.payout.amount ?? 0).toFixed(2)} to ${payoutActionDialog.payout.affiliateEmail}?`
                  : payoutActionDialog.action === 'reject'
                  ? `Reject payout request from ${payoutActionDialog.payout.affiliateEmail}?`
                  : `Mark payout as PAID ($${(payoutActionDialog.payout.amount ?? 0).toFixed(2)} to ${payoutActionDialog.payout.affiliateEmail})?`
              }
              confirmText={
                payoutActionDialog.action === 'approve'
                  ? 'Approve'
                  : payoutActionDialog.action === 'reject'
                  ? 'Reject'
                  : 'Mark as Paid'
              }
              variant={payoutActionDialog.action === 'reject' ? 'danger' : 'default'}
            />
          )}

          {/* Step 2: Get notes (for reject/pay actions) */}
          {showPayoutNotesDialog && (
            <PromptDialog
              open={showPayoutNotesDialog}
              onOpenChange={(open) => {
                if (!open) {
                  setShowPayoutNotesDialog(false);
                  setPayoutActionDialog({ open: false, payout: null, action: null });
                }
              }}
              onConfirm={handlePayoutActionSubmit}
              title={
                payoutActionDialog.action === 'reject'
                  ? 'Rejection Reason'
                  : 'Payment Reference'
              }
              description={
                payoutActionDialog.action === 'reject'
                  ? 'Enter reason for rejection:'
                  : 'Enter payment reference/notes (optional):'
              }
              placeholder={
                payoutActionDialog.action === 'reject'
                  ? 'Reason for rejection...'
                  : 'Payment reference...'
              }
              confirmText={
                payoutActionDialog.action === 'reject' ? 'Reject Payout' : 'Confirm Payment'
              }
              required={payoutActionDialog.action === 'reject'}
              inputType="textarea"
              variant={payoutActionDialog.action === 'reject' ? 'default' : 'success'}
            />
          )}
        </>
      )}

      {/* Create Affiliate Modal */}
      {showCreateAffiliateModal && (
        <CreateAffiliateModal onClose={() => setShowCreateAffiliateModal(false)} />
      )}
    </div>
  );
};

// Stats Card Component
interface StatCardProps {
  label: string;
  value: string | number;
  color: string;
}

const StatCard = ({ label, value, color }: StatCardProps) => (
  <div className="bg-white p-6 rounded-lg border border-gray-200">
    <div className="text-sm text-gray-500 mb-1">{label}</div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
  </div>
);

// Affiliates Tab Component
interface AffiliatesTabProps {
  filters: AdminAffiliateFilters;
  setFilters: (filters: AdminAffiliateFilters) => void;
  search: string;
  setSearch: (search: string) => void;
  data: { data: AdminAffiliateListItem[]; meta: any } | undefined;
  isLoading: boolean;
  error: Error | null;
  onSearch: (e: React.FormEvent) => void;
  onStatusChange: (
    affiliate: AdminAffiliateListItem,
    status: (typeof AFFILIATE_STATUSES)[number]
  ) => void;
  onPageChange: (page: number) => void;
  formatDateTime: (date: string) => string;
  formatDateOnly: (date: string) => string;
  updateStatusPending: boolean;
}

const AffiliatesTab = ({
  filters,
  setFilters,
  search,
  setSearch,
  data,
  isLoading,
  error,
  onSearch,
  onStatusChange,
  onPageChange,
  formatDateOnly,
  updateStatusPending,
}: AffiliatesTabProps) => {
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading affiliates: {formatApiError(error).message}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Search & Filters */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <form onSubmit={onSearch} className="flex gap-4 flex-wrap">
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px]"
          />
          <Select
            value={filters.status || ''}
            onValueChange={(value) =>
              setFilters({
                ...filters,
                status: (value as AdminAffiliateFilters['status']) || undefined,
                page: 1,
              })
            }
            placeholder="All Statuses"
            options={AFFILIATE_STATUSES.map((status) => ({
              value: status,
              label: status.charAt(0).toUpperCase() + status.slice(1),
            }))}
            clearable
            clearLabel="All Statuses"
            className="min-w-[150px]"
          />
        </form>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-8 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Affiliate
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Referral Code
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                    Referrals
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                    Earnings
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                    Pending
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Joined
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(data?.data ?? []).map((affiliate) => (
                  <tr key={affiliate.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900">
                          {affiliate.userName || 'No name'}
                        </div>
                        <div className="text-sm text-gray-500">{affiliate.userEmail}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                        {affiliate.referralCode}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={affiliate.status}
                        onValueChange={(value) =>
                          onStatusChange(
                            affiliate,
                            value as (typeof AFFILIATE_STATUSES)[number]
                          )
                        }
                        options={AFFILIATE_STATUSES.map((status) => ({
                          value: status,
                          label: status.charAt(0).toUpperCase() + status.slice(1),
                        }))}
                        disabled={updateStatusPending}
                        size="sm"
                        className={statusColors[affiliate.status] || 'bg-gray-100'}
                      />
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      {affiliate.totalConversions}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      ${(affiliate.totalCommissions ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      ${(affiliate.pendingPayout ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDateOnly(affiliate.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <ActionButton
                          icon={ICONS.EYE}
                          tooltip="View Details"
                          href={`/admin/affiliates/${affiliate.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {(data?.data?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState title="No affiliates found" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.meta && data.meta.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(data.meta.page - 1) * data.meta.limit + 1} to{' '}
                {Math.min(data.meta.page * data.meta.limit, data.meta.total)} of {data.meta.total}
              </div>
              <Pagination page={data.meta.page} totalPages={data.meta.totalPages} onPageChange={onPageChange} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Payouts Tab Component
interface PayoutsTabProps {
  filters: AdminPayoutFilters;
  setFilters: (filters: AdminPayoutFilters) => void;
  data: { data: AdminPayoutListItem[]; meta: any } | undefined;
  isLoading: boolean;
  error: Error | null;
  onProcessPayout: (
    payout: AdminPayoutListItem,
    action: 'approve' | 'reject' | 'pay'
  ) => void;
  onPageChange: (page: number) => void;
  formatDateTime: (date: string) => string;
  formatDateOnly: (date: string) => string;
  processPayoutPending: boolean;
}

const PayoutsTab = ({
  filters,
  setFilters,
  data,
  isLoading,
  error,
  onProcessPayout,
  onPageChange,
  formatDateTime,
  processPayoutPending,
}: PayoutsTabProps) => {
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading payouts: {formatApiError(error).message}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex gap-4 flex-wrap">
          <Select
            value={filters.status || ''}
            onValueChange={(value) =>
              setFilters({
                ...filters,
                status: (value as AdminPayoutFilters['status']) || undefined,
                page: 1,
              })
            }
            placeholder="All Statuses"
            options={PAYOUT_STATUSES.map((status) => ({
              value: status,
              label: status.charAt(0).toUpperCase() + status.slice(1),
            }))}
            clearable
            clearLabel="All Statuses"
            className="min-w-[150px]"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-8 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Affiliate
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                    Amount
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Requested
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Processed
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                    Notes
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(data?.data ?? []).map((payout) => (
                  <tr key={payout.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900">
                          {payout.affiliateName || 'No name'}
                        </div>
                        <div className="text-sm text-gray-500">{payout.affiliateEmail}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                      ${(payout.amount ?? 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={payoutStatusVariant(payout.status)}>
                        {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDateTime(payout.requestedAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {payout.processedAt ? formatDateTime(payout.processedAt) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {payout.adminNotes || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {payout.status === 'requested' && (
                          <>
                            <ActionButton
                              icon={ICONS.CHECK_CIRCLE}
                              tooltip="Approve"
                              onClick={() => onProcessPayout(payout, 'approve')}
                              variant="success"
                              disabled={processPayoutPending}
                            />
                            <ActionButton
                              icon={ICONS.CROSS_CIRCLE}
                              tooltip="Reject"
                              onClick={() => onProcessPayout(payout, 'reject')}
                              variant="danger"
                              disabled={processPayoutPending}
                            />
                          </>
                        )}
                        {payout.status === 'approved' && (
                          <ActionButton
                            icon="dollar-on-hand"
                            tooltip="Mark as Paid"
                            onClick={() => onProcessPayout(payout, 'pay')}
                            variant="success"
                            disabled={processPayoutPending}
                          />
                        )}
                        {(payout.status === 'paid' || payout.status === 'rejected') && (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {(data?.data?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState title="No payouts found" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.meta && data.meta.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(data.meta.page - 1) * data.meta.limit + 1} to{' '}
                {Math.min(data.meta.page * data.meta.limit, data.meta.total)} of {data.meta.total}
              </div>
              <Pagination page={data.meta.page} totalPages={data.meta.totalPages} onPageChange={onPageChange} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const payoutStatusVariant = (status: string): 'success' | 'info' | 'primary' | 'danger' | 'neutral' => {
  switch (status) {
    case 'requested':
      return 'info';
    case 'approved':
      return 'success';
    case 'paid':
      return 'primary';
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
};

// ============================================================================
// LOCAL MODAL: Create Affiliate
// ============================================================================

interface CreateAffiliateModalProps {
  onClose: () => void;
}

const CreateAffiliateModal = ({ onClose }: CreateAffiliateModalProps) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userInputValue, setUserInputValue] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teamInputValue, setTeamInputValue] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [minPayout, setMinPayout] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  const userSearchQuery = useDebounce(userInputValue, 300);
  const teamSearchQuery = useDebounce(teamInputValue, 300);

  const { data: usersData, isLoading: isLoadingUsers } = useAdminUsers({
    search: userSearchQuery || undefined,
    limit: 50,
    isActive: true,
  });

  const { data: teamsData, isLoading: isLoadingTeams } = useAdminTeams({
    search: teamSearchQuery || undefined,
    limit: 50,
  });

  const createAffiliate = useCreateAffiliate();

  const userOptions = (usersData?.data ?? []).map(u => ({
    value: String(u.id),
    label: u.fullName ? `${u.fullName} (${u.email})` : u.email,
  }));

  const teamOptions = (teamsData?.data ?? []).map(t => ({
    value: String(t.id),
    label: t.name,
  }));

  const handleUserComboboxChange = (val: string) => {
    setInlineError(null);
    if (!isNaN(parseInt(val, 10))) {
      setSelectedUserId(val);
      const picked = userOptions.find(o => o.value === val);
      setUserInputValue(picked?.label ?? val);
    } else {
      setSelectedUserId('');
      setUserInputValue(val);
    }
  };

  const handleTeamComboboxChange = (val: string) => {
    if (!isNaN(parseInt(val, 10))) {
      setSelectedTeamId(val);
      const picked = teamOptions.find(o => o.value === val);
      setTeamInputValue(picked?.label ?? val);
    } else {
      setSelectedTeamId('');
      setTeamInputValue(val);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);

    const parsedUserId = parseInt(selectedUserId, 10);
    if (isNaN(parsedUserId)) {
      setInlineError('Please select a user from the list.');
      return;
    }

    const parsedTeamId = selectedTeamId ? parseInt(selectedTeamId, 10) : undefined;
    const parsedRate = commissionRate.trim() ? parseFloat(commissionRate) : undefined;
    const parsedMinPayout = minPayout.trim() ? parseFloat(minPayout) : undefined;

    if (parsedRate !== undefined && (isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100)) {
      setInlineError('Commission rate must be between 0 and 100.');
      return;
    }

    if (parsedMinPayout !== undefined && (isNaN(parsedMinPayout) || parsedMinPayout < 0)) {
      setInlineError('Min payout must be a positive number.');
      return;
    }

    try {
      await createAffiliate.mutateAsync({
        userId: parsedUserId,
        teamId: parsedTeamId,
        referralCode: referralCode.trim() || undefined,
        customCommissionRate: parsedRate,
        minPayout: parsedMinPayout,
      });
      toast.success('Affiliate created');
      onClose();
    } catch (err) {
      const apiErr = formatApiError(err);
      if (apiErr.status === 409) {
        setInlineError('This user is already an affiliate.');
      } else {
        toast.error(apiErr.message);
      }
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Create Affiliate"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={createAffiliate.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="create-affiliate-form" disabled={!selectedUserId || createAffiliate.isPending} loading={createAffiliate.isPending}>
            Create Affiliate
          </Button>
        </ModalFooter>
      }
    >
      <form id="create-affiliate-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User <span className="text-red-500">*</span></label>
          <Combobox
            value={userInputValue}
            onChange={handleUserComboboxChange}
            options={userOptions}
            placeholder="Search by name or email…"
            loading={isLoadingUsers}
            emptyMessage="No matching users"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team (optional)</label>
          <Combobox
            value={teamInputValue}
            onChange={handleTeamComboboxChange}
            options={teamOptions}
            placeholder="Search teams… (defaults to user's home team)"
            loading={isLoadingTeams}
            emptyMessage="No matching teams"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Referral Code (optional)</label>
          <input
            type="text"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            placeholder="Leave blank to auto-generate"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
          />
          <p className="text-xs text-gray-500 mt-1">If left blank, a unique code will be generated automatically.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Commission Rate % (optional)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={commissionRate}
            onChange={(e) => setCommissionRate(e.target.value)}
            placeholder="e.g. 20 — leave blank for system default"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min Payout (optional)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={minPayout}
            onChange={(e) => setMinPayout(e.target.value)}
            placeholder="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
          />
        </div>

        {inlineError && <p className="text-sm text-red-600">{inlineError}</p>}
      </form>
    </Modal>
  );
};

export default AdminAffiliates;
